import { html, css, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OAuth2Client, OAuthStatusTypes } from '@trumpf/ux.utils';
import authConfig from './config';

const oauthClient = new OAuth2Client(authConfig);

const redirectToAuthCallbackUrl = () => {
  const { location } = window.top;
  const params = new URLSearchParams(location.search);
  const path = params.get('path');
  const cleanUrl = `${location.protocol}//${location.host}${location.pathname}?path=${path}`;
  if (window.top.location.href !== cleanUrl) {
    window.top.history.replaceState({}, document.title, cleanUrl);
  }
};

@customElement('demo-auth')
export class DemoAuth extends LitElement {
  @property({ type: Boolean }) isLoggedIn = oauthClient.getStatus() === OAuthStatusTypes.AUTHENTICATED;
  @property({ type: Boolean }) isPending = oauthClient.getStatus() === OAuthStatusTypes.PENDING;
  @property({ type: Date }) expiration = new Date(oauthClient.getTokenExpiration());
  unsubscribe: () => void;

  connectedCallback() {
    super.connectedCallback();
    const update = () => {
      const status = oauthClient.getStatus();
      this.isLoggedIn = status === OAuthStatusTypes.AUTHENTICATED;
      this.isPending = status === OAuthStatusTypes.PENDING;
      this.expiration = new Date(oauthClient.getTokenExpiration());
      // console.log(`[OAUTH] status: ${status}`);
    };
    update();
    this.unsubscribe = oauthClient.subscribe(update);
    this.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleClick);
    this.unsubscribe();
  }

  handleClick = () => {
    if (this.isLoggedIn) {
      this.handleLogout();
    } else {
      this.handleLogin();
    }
  };

  handleLogin = () => {
    this.isPending = true;
    oauthClient.login();
  };

  handleLogout = () => {
    this.isPending = true;
    oauthClient.logout();
  };

  handleDeleteToken = () => {
    oauthClient.deleteAccessToken();
  };

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has('isLoggedIn') || changedProperties.has('isPending')) {
      if (!this.isPending) {
        redirectToAuthCallbackUrl();
      }
    }
  }

  static styles = css`
    .auth {
      padding: 20px 15px;
      background-color: rgb(246, 249, 252);
      box-shadow: rgba(0, 0, 0, 0.1) 0 1px 3px 1px;
      border-radius: 4px;
      margin-bottom: 20px;
    }
    .login-status {
      font-family: var(--ux-font);
      font-weight: var(--ux-button-font-weight);
      font-size: 16px;
    }
  `;

  render() {
    return html`
      <div class="auth">
        ${this.isLoggedIn && !this.isPending
          ? html`
              <div class="login-status">Logged In</div>
              <br />
              <div class="ux-button-group">
                <button type="button" class="ux-button" @click=${this.handleLogout} ?disabled=${this.isPending}>Logout</button>
                <button type="button" class="ux-button" @click=${this.handleDeleteToken} ?disabled=${this.isPending}>Delete Token</button>
              </div>
              <div class="ux-u-margin-top-20">
                <div>Token expires at ${this.expiration.toLocaleString()}</div>
              </div>
            `
          : ''}
        ${!this.isLoggedIn
          ? html`
              <div class="login-status">Logged Out</div>
              <br />
              <button type="button" class="ux-button" @click=${this.handleLogin} ?disabled=${this.isPending}>Login</button>
              <div class="ux-u-margin-top-20">
                <div>Login at MyTRUMPF (QA) via Authorization Code Flow + PKCE</div>
              </div>
            `
          : ''}
        ${this.isPending ? html` <ux-loader variant="horizontal"> </ux-loader> ` : ''}
      </div>
    `;
  }
}

export default DemoAuth;
